import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import {
  ArrowRight, Car, Check, Droplets, Home, MapPin, MonitorSmartphone,
  Navigation, ShieldCheck, Sparkles,
} from "lucide-react";
import { Reveal } from "../components/ui-bits";
import { scrollToId, useApp } from "../context/AppContext";

const CARS = [
  "Honda City", "Hyundai Creta", "Tata Nexon", "Tata Nexon EV", "Kia Seltos",
  "Maruti Grand Vitara", "Hyundai Verna", "Mahindra XUV700", "MG ZS EV",
];
const PLACES = [
  "Sector 119, Noida", "Sector 120, Noida", "Sector 121, Noida", "Sector 122, Noida",
  "Sector 137, Noida", "Sector 150, Noida", "Greater Noida West",
];
const STATUS_META = {
  "Service Completed": { icon: Check, cls: "border-emerald-200 bg-emerald-50 text-emerald-600" },
  "Service In Progress": { icon: Sparkles, cls: "border-violet-200 bg-violet-50 text-violet-600" },
  "Partner En Route": { icon: Navigation, cls: "border-amber-200 bg-amber-50 text-amber-600" },
};
const STATUS_POOL = ["Partner En Route", "Service In Progress", "Service Completed", "Service Completed"];

const BASE_COUNT = 1428;
const rand = (min, max) => min + Math.random() * (max - min);
const pick = (arr, avoid) => {
  let v = arr[Math.floor(Math.random() * arr.length)];
  if (avoid && arr.length > 1 && v === avoid) v = arr[(arr.indexOf(v) + 1) % arr.length];
  return v;
};
const agoLabel = (ts, now) => {
  const m = Math.max(0, Math.round((now - ts) / 60000));
  if (m < 1) return "Just now";
  if (m === 1) return "1 min ago";
  return `${m} mins ago`;
};

const SEED = [
  { car: "Honda City", place: "Sector 121, Noida", status: "Service Completed", minsAgo: 0 },
  { car: "Hyundai Creta", place: "Sector 120, Noida", status: "Service In Progress", minsAgo: 2 },
  { car: "Tata Nexon EV", place: "Sector 119, Noida", status: "Service Completed", minsAgo: 4 },
  { car: "Kia Seltos", place: "Sector 122, Noida", status: "Partner En Route", minsAgo: 6 },
  { car: "Maruti Grand Vitara", place: "Sector 120, Noida", status: "Service Completed", minsAgo: 9 },
];

const DOTS = [
  { label: "119", x: "14%", y: "38%" },
  { label: "120", x: "38%", y: "62%" },
  { label: "121", x: "58%", y: "30%" },
  { label: "122", x: "80%", y: "55%" },
  { label: "137", x: "64%", y: "78%" },
];

function LiveCounter({ value, started }) {
  const [shown, setShown] = useState(() => Math.max(0, value - 14));
  const shownRef = useRef(shown);
  useEffect(() => {
    if (!started) return;
    let raf;
    const t0 = performance.now();
    const from = shownRef.current;
    const to = value;
    const dur = Math.max(500, Math.min(1600, Math.abs(to - from) * 110));
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      const v = Math.round(from + (to - from) * e);
      shownRef.current = v;
      setShown(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, value]);
  return (
    <span className="font-display text-5xl font-bold tracking-tight text-nighttext sm:text-6xl" data-testid="live-counter-value">
      {shown.toLocaleString("en-IN")}<span className="text-carza">+</span>
    </span>
  );
}

export default function LiveActivity() {
  const { openBooking } = useApp();
  const [count, setCount] = useState(() => {
    const v = parseInt(sessionStorage.getItem("carza_cars_today") || "", 10);
    return Number.isFinite(v) && v >= BASE_COUNT ? v : BASE_COUNT;
  });
  const [events, setEvents] = useState(() => SEED.map((s, i) => ({ id: i, ...s, ts: Date.now() - s.minsAgo * 60000 })));
  const [now, setNow] = useState(Date.now());
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const [started, setStarted] = useState(false);
  const idRef = useRef(SEED.length);
  const viewRef = useRef(null);
  const inView = useInView(viewRef, { once: true, margin: "-15% 0px" });

  useEffect(() => { sessionStorage.setItem("carza_cars_today", String(count)); }, [count]);

  // animate the counter when the section first enters the viewport
  useEffect(() => {
    if (inView) setStarted(true);
  }, [inView]);

  // natural counter ticks: +1 every 20–60s, occasionally +2
  useEffect(() => {
    let alive = true;
    let timer;
    const schedule = () => {
      timer = setTimeout(() => {
        if (!alive) return;
        setCount((c) => c + (Math.random() < 0.2 ? 2 : 1));
        setLastUpdated(Date.now());
        schedule();
      }, rand(20000, 60000));
    };
    schedule();
    return () => { alive = false; clearTimeout(timer); };
  }, []);

  // feed rotation: new simulated event every 15–30s, keep max 6
  useEffect(() => {
    let alive = true;
    let timer;
    const schedule = () => {
      timer = setTimeout(() => {
        if (!alive) return;
        setEvents((list) => {
          const next = {
            id: idRef.current++,
            car: pick(CARS, list[0]?.car),
            place: pick(PLACES, list[0]?.place),
            status: STATUS_POOL[Math.floor(Math.random() * STATUS_POOL.length)],
            ts: Date.now() - rand(0, 20000),
          };
          return [next, ...list].slice(0, 6);
        });
        schedule();
      }, rand(15000, 30000));
    };
    schedule();
    return () => { alive = false; clearTimeout(timer); };
  }, []);

  // refresh relative timestamps
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(i);
  }, []);

  const updatedLabel = now - lastUpdated < 45000 ? "Updated just now" : "Updated a few mins ago";

  return (
    <section className="light-section relative py-20 lg:py-28" data-testid="live-activity-section" ref={viewRef}>
      <div className="dot-grid-light pointer-events-none absolute inset-0" />

      <div className="container-x relative flex flex-col gap-10 lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-16 lg:gap-y-12">
        {/* headline */}
        <div className="order-1 lg:col-start-1 lg:row-start-1">
          <Reveal><span className="eyebrow eyebrow-dark">Live Car Care Activity</span></Reveal>
          <Reveal delay={0.06}>
            <h2 className="mt-4 font-display text-4xl font-bold leading-[1.06] tracking-tight text-nighttext sm:text-5xl" data-testid="live-headline">
              CARZA IS MOVING. <span className="text-carza">YOUR CAR CAN TOO.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#5c5470] sm:text-lg" data-testid="live-sub">
              People across Noida &amp; Delhi NCR are already getting their cars cared for.
            </p>
          </Reveal>
        </div>

        {/* acquisition CTA */}
        <Reveal delay={0.16} className="order-3 lg:col-start-1 lg:row-start-2">
            <div className="rounded-3xl border border-[#e9e3f8] bg-white/95 p-6 shadow-[0_25px_60px_-30px_rgba(23,19,31,0.35)] backdrop-blur sm:p-7" data-testid="live-acquisition-card">
              <p className="font-display text-2xl font-bold tracking-tight text-nighttext sm:text-3xl" data-testid="live-next-title">
                Your Car Could Be <span className="text-carza">Next.</span>
              </p>
              <p className="mt-3 text-[15px] leading-relaxed text-[#5c5470]">
                Book professional car care at your home, office or society — without the hassle of traditional car washing.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button onClick={() => openBooking()} className="btn btn-primary flex-1 px-6 py-3.5" data-testid="live-book-cta">
                  Book a Car Care <ArrowRight size={16} />
                </button>
                <button onClick={() => scrollToId("services")} className="btn btn-outline-dark flex-1 px-6 py-3.5" data-testid="live-explore-cta">
                  Explore Services
                </button>
              </div>
            </div>
          </Reveal>

        {/* trust strip */}
        <Reveal delay={0.2} className="order-4 lg:col-start-1 lg:row-start-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="live-trust-strip">
              {[
                { icon: Home, label: "Doorstep Service" },
                { icon: ShieldCheck, label: "Trained Partners" },
                { icon: Droplets, label: "Water-Efficient Process" },
                { icon: MonitorSmartphone, label: "Easy Online Booking" },
              ].map((t) => (
                <div key={t.label} className="flex items-center gap-3 rounded-2xl border border-[#eee9f9] bg-white px-4 py-3.5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-carza/10 text-carza">
                    <Check size={14} />
                  </span>
                  <span className="text-[14px] font-semibold text-nighttext">{t.label}</span>
                </div>
              ))}
            </div>
        </Reveal>

        {/* live activity card */}
        <Reveal delay={0.1} className="order-2 lg:col-start-2 lg:row-span-3 lg:row-start-1">
          <div className="overflow-hidden rounded-[2rem] border border-[#e9e3f8] bg-white shadow-[0_35px_80px_-40px_rgba(23,19,31,0.4)]" data-testid="live-activity-card">
            {/* header */}
            <div className="border-b border-[#f0ecfa] bg-gradient-to-br from-[#f7f4ff] to-white px-6 pb-6 pt-5">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-nighttext">
                  <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  </span>
                  Live Car Care
                </span>
                <span className="rounded-full border border-[#e9e3f8] bg-white px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#8b83a3]">
                  Demo live activity
                </span>
              </div>
              <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8b83a3]">Cars cared for today</p>
              <div className="mt-1 flex items-end justify-between gap-4">
                <LiveCounter value={count} started={started} />
                <span className="flex items-center gap-1.5 pb-2 text-[11px] text-[#8b83a3]" data-testid="live-updated">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {updatedLabel}
                </span>
              </div>
            </div>

            {/* feed */}
            <div className="px-6 py-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8b83a3]">Live order feed</p>
              <ul className="mask-fade-b mt-4 flex max-h-[336px] flex-col gap-2 overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-lenis-prevent data-testid="live-feed">
                <AnimatePresence initial={false}>
                  {events.map((e) => {
                    const meta = STATUS_META[e.status];
                    const Icon = meta.icon;
                    return (
                      <motion.li
                        key={e.id}
                        layout
                        initial={{ opacity: 0, y: -18, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ type: "spring", stiffness: 260, damping: 26 }}
                        className="flex items-center gap-3 rounded-2xl border border-[#f0ecfa] bg-[#fbfaff] px-4 py-3"
                        data-testid="live-feed-item"
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-carza/10 text-carza">
                          <Car size={16} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[14px] font-semibold text-nighttext">{e.car}</span>
                          <span className="mt-0.5 flex items-center gap-1 text-[12px] text-[#8b83a3]">
                            <MapPin size={10} /> {e.place}
                          </span>
                        </span>
                        <span className="flex shrink-0 flex-col items-end gap-1.5">
                          <span className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${meta.cls}`}>
                            <Icon size={10} /> {e.status}
                          </span>
                          <span className="font-mono text-[10px] text-[#8b83a3]">{agoLabel(e.ts, now)}</span>
                        </span>
                      </motion.li>
                    );
                  })}
                </AnimatePresence>
              </ul>
            </div>

            {/* stylized activity network */}
            <div className="border-t border-[#f0ecfa] px-6 py-5">
              <div className="relative h-28 overflow-hidden rounded-2xl bg-gradient-to-br from-[#f3efff] to-[#ece5ff]" data-testid="live-network-map">
                <div className="absolute inset-y-0 left-[30%] w-px bg-white/80" aria-hidden="true" />
                <div className="absolute inset-y-0 left-[62%] w-px bg-white/80" aria-hidden="true" />
                <div className="absolute inset-x-0 top-[45%] h-px bg-white/80" aria-hidden="true" />
                <div className="absolute inset-x-0 top-[72%] h-px bg-white/60" aria-hidden="true" />
                {DOTS.map((d, i) => (
                  <span key={d.label} className="absolute" style={{ left: d.x, top: d.y }} aria-hidden="true">
                    <motion.span
                      className="absolute -inset-1.5 rounded-full bg-carza"
                      animate={{ scale: [1, 2.4], opacity: [0.45, 0] }}
                      transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.45, ease: "easeOut" }}
                    />
                    <span className="relative block h-2 w-2 rounded-full bg-carza shadow-[0_0_10px_rgba(109,61,245,0.8)]" />
                  </span>
                ))}
                <span className="absolute bottom-2 left-3 font-mono text-[9px] uppercase tracking-[0.2em] text-[#6F6890]">
                  Carza Activity Network
                </span>
              </div>
              <p className="mt-3 text-center text-[10px] text-[#a49cb8]" data-testid="live-disclaimer">
                Live activity shown for demonstration purposes.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
